import { useCallback, useEffect, useMemo, useRef, useState, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useAuth } from '@/lib/auth-context'

type BearerHeaders = { Authorization: string } | null

export function useBearer(): BearerHeaders {
  const { session } = useAuth()
  const token = session?.access_token || null
  return useMemo(() => (token ? { Authorization: `Bearer ${token}` } : null), [token])
}

interface FetchJSONOptions {
  bearer?: Record<string, string> | null
  method?: string
  body?: unknown
  signal?: AbortSignal
  headers?: Record<string, string>
}

interface FetchJSONError extends Error {
  status?: number
  payload?: unknown
}

export async function fetchJSON(url: string, { bearer, method = 'GET', body, signal, headers = {} }: FetchJSONOptions = {}): Promise<unknown> {
  if (!bearer?.Authorization) {
    throw new Error('Not authenticated')
  }

  const reqHeaders: Record<string, string> = {
    ...headers,
    ...bearer,
  }

  if (body !== undefined) {
    reqHeaders['content-type'] = 'application/json'
  }

  const init: RequestInit = {
    method,
    headers: reqHeaders,
    signal,
  }

  if (body !== undefined) {
    init.body = typeof body === 'string' ? body : JSON.stringify(body)
  }

  const response = await fetch(url, init)
  let payload = null

  try {
    const text = await response.text()
    payload = text ? JSON.parse(text) : null
  } catch (err: unknown) {
    if ((err as Error)?.name !== 'SyntaxError') throw err
  }

  if (!response.ok) {
    const error: FetchJSONError = new Error(payload?.error || response.statusText || 'Request failed')
    error.status = response.status
    error.payload = payload
    throw error
  }

  return payload
}

/**
 * Build the Render core-api admin URL and fetch, unwrapping the { data, error } envelope.
 * All admin endpoints on Render live at /api/v1/admin/...
 */
export async function adminApi<T = unknown>(
  path: string,
  opts: Omit<FetchJSONOptions, 'bearer'> & { bearer?: Record<string, string> | null } = {},
): Promise<T> {
  const url = `/api/v1/admin${path}`
  const payload = (await fetchJSON(url, opts)) as { data?: T; error?: { message?: string } | string | null } | null
  if (payload && payload.error) {
    const msg = typeof payload.error === 'string' ? payload.error : payload.error?.message || 'Request failed'
    throw new Error(msg)
  }
  return ((payload as Record<string, unknown>)?.data ?? payload) as T
}

export function useDebouncedValue<T>(value: T, delay = 300): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay)
    return () => clearTimeout(id)
  }, [value, delay])
  return v
}

type PagedFetcher<T> = (params: { page: number; pageSize: number; signal: AbortSignal }) => Promise<{ items: T[]; total: number }>

export function usePagedQuery<T>(fetcher: PagedFetcher<T>, deps: unknown[] = [], initialPageSize = 20) {
  const [items, setItems] = useState<T[]>([])
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const controllerRef = useRef<AbortController | null>(null)
  const pageRef = useRef(1)
  const pageSizeRef = useRef(initialPageSize)

  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort()
        controllerRef.current = null
      }
    }
  }, [])

  const load = useCallback(
    async (nextPage = pageRef.current, nextPageSize = pageSizeRef.current) => {
      if (controllerRef.current) {
        controllerRef.current.abort()
      }

      const controller = new AbortController()
      controllerRef.current = controller

      setLoading(true)
      setError(null)

      try {
        const result = await fetcher({ page: nextPage, pageSize: nextPageSize, signal: controller.signal })
        if (controller.signal.aborted) return

        const safeItems = Array.isArray(result?.items) ? result.items : []
        const safeTotal = typeof result?.total === 'number' ? result.total : 0

        setItems(safeItems)
        setTotal(safeTotal)
        setPage(nextPage)
        setPageSize(nextPageSize)
        pageRef.current = nextPage
        pageSizeRef.current = nextPageSize
      } catch (loadError: unknown) {
        if ((loadError as Error)?.name === 'AbortError') return
        setError((loadError as Error)?.message || 'Failed to load')
        setItems([])
        setTotal(0)
      } finally {
        if (controllerRef.current === controller) {
          controllerRef.current = null
        }
        if (!controller.signal.aborted) {
          setLoading(false)
        }
      }
    },
    [fetcher],
  )

  useEffect(() => {
    load(1, pageSizeRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  const pageCount = useMemo(() => Math.max(1, Math.ceil((total || 0) / pageSize)), [total, pageSize])

  return {
    items,
    setItems,
    page,
    pageSize,
    setPage,
    setPageSize,
    total,
    pageCount,
    loading,
    error,
    load,
  }
}

export function RoleBadge({ role }: { role: string | null | undefined }) {
  if (!role) return <span className="text-cocoa/60">—</span>
  const color =
    role === 'superadmin'
      ? 'bg-purple-100 text-purple-700'
      : role === 'admin'
        ? 'bg-indigo-100 text-indigo-700'
        : role === 'teacher'
          ? 'bg-blue-100 text-blue-700'
          : role === 'sponsor'
            ? 'bg-amber-100 text-amber-700'
            : 'bg-gray-200 text-gray-700'
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide ${color}`}>{role}</span>
}

export function Section({ title, actions, children }: { title: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-2xl bg-white shadow-sm border border-white/70 p-4 md:p-6 space-y-4 min-w-0">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <a
            href="/portal/admin"
            className="text-xs md:text-sm rounded border border-white/70 bg-white px-3 py-1 font-medium text-cocoa transition hover:bg-shell/40"
            aria-label="Back to Admin Home"
          >
            ← Admin
          </a>
          <h2 className="text-xl font-semibold text-cocoa">{title}</h2>
        </div>
        {actions}
      </div>
      <div className="min-w-0 overflow-visible">{children}</div>
    </section>
  )
}

// super-light toast (non-blocking)
export function toast(msg: string) {
  const el = document.createElement('div')
  el.textContent = msg
  el.className =
    'fixed z-[1000] bottom-4 left-1/2 -translate-x-1/2 bg-black text-white text-xs md:text-sm px-3 py-2 rounded shadow'
  document.body.appendChild(el)
  setTimeout(() => el.remove(), 1800)
}

export function getMagicLinkEndpoint() {
  const CORE_API = import.meta.env.VITE_CORE_API_URL || ''
  return `${CORE_API}/api/v1/auth/request-link`
}

export function ToolbarInput({ value, onChange, placeholder = 'Search…', type = 'text' }: { value: string; onChange: (value: string) => void; placeholder?: string; type?: string }) {
  return (
    <input
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      type={type}
      className="w-full rounded-lg border border-white/70 bg-shell/40 px-3 py-2 text-sm text-cocoa placeholder:text-cocoa/50 focus:border-terracotta focus:outline-none"
    />
  )
}

export function ToolbarSelect({ value, onChange, options }: { value: string; onChange: (value: string) => void; options: Array<{ value: string; label: string }> }) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="w-full rounded-lg border border-white/70 bg-shell/40 px-3 py-2 text-sm text-cocoa focus:border-terracotta focus:outline-none"
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  )
}

export function Pager({ page, pageCount, onPage }: { page: number; pageCount: number; onPage: (page: number) => void }) {
  return (
    <div className="flex items-center gap-2 text-sm text-cocoa">
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
        className="rounded-full border border-white/70 bg-white px-3 py-1.5 font-medium disabled:opacity-50"
      >
        Previous
      </button>
      <span className="text-xs uppercase tracking-wide text-cocoa/60">
        Page {page} of {pageCount}
      </span>
      <button
        type="button"
        disabled={page >= pageCount}
        onClick={() => onPage(page + 1)}
        className="rounded-full border border-white/70 bg-white px-3 py-1.5 font-medium disabled:opacity-50"
      >
        Next
      </button>
    </div>
  )
}

export function TableScroller({ children, minWidth = 720 }: { children: ReactNode; minWidth?: number }) {
  return (
    <div className="w-full overflow-x-auto overscroll-x-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
      <div className="inline-block align-top" style={{ minWidth }}>
        {children}
      </div>
    </div>
  )
}

export function useToast() {
  const [msg, setMsg] = useState<string | null>(null)
  const show = (text: string) => {
    setMsg(text)
    setTimeout(() => setMsg(null), 2000)
  }
  const ToastHost = () =>
    msg
      ? createPortal(
          <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[1000]">
            <div className="px-3 py-2 rounded bg-emerald-600 text-white shadow">{msg}</div>
          </div>,
          document.body
        )
      : null
  return { show, ToastHost }
}
